// @ts-nocheck
import { useState, useEffect, useRef, useCallback } from "react";
import { apiService } from "../../services/api";

const NODE_COLORS = {
  concept: "#6366f1",
  verbatim_chunk: "#10b981",
  fact: "#f59e0b",
  decision_outcome: "#ef4444",
  reference: "#8b5cf6",
  metric: "#06b6d4",
  category: "#ec4899",
  constant: "#64748b",
  and_gate: "#f97316",
  or_gate: "#f97316",
};

type NodeNeighborhoodGraphProps = {
  nodeId: string;
  graphId: string;
  onNodeClick?: (nodeId: string) => void;
};

const NodeNeighborhoodGraph = ({ nodeId, graphId, onNodeClick }: NodeNeighborhoodGraphProps) => {
  const svgRef = useRef(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [depth, setDepth] = useState(1);

  const fetchNeighborhood = useCallback(async () => {
    if (!nodeId || !graphId) return;
    setLoading(true);
    try {
      const synapseRes: any = await apiService.listSynapses({ graphId, page: 1, pageSize: 200 });
      const allSynapses = synapseRes?.synapses || synapseRes?.data || [];

      const connected = allSynapses.filter(
        (s) => s.sourceNodeId === nodeId || s.targetNodeId === nodeId
      );

      const neighborIds = new Set();
      neighborIds.add(nodeId);
      connected.forEach((s) => {
        neighborIds.add(s.sourceNodeId);
        neighborIds.add(s.targetNodeId);
      });

      let hop2Synapses = [];
      if (depth >= 2) {
        hop2Synapses = allSynapses.filter(
          (s) =>
            (neighborIds.has(s.sourceNodeId) || neighborIds.has(s.targetNodeId)) &&
            !(s.sourceNodeId === nodeId || s.targetNodeId === nodeId)
        );
        hop2Synapses.forEach((s) => {
          neighborIds.add(s.sourceNodeId);
          neighborIds.add(s.targetNodeId);
        });
      }

      const allEdges = [...connected, ...hop2Synapses];
      const nodeMap = {};
      allEdges.forEach((s) => {
        if (!nodeMap[s.sourceNodeId]) nodeMap[s.sourceNodeId] = { id: s.sourceNodeId, label: s.sourceNodeName || s.sourceNodeId?.substring(0, 8), type: s.sourceNodeType || "concept" };
        if (!nodeMap[s.targetNodeId]) nodeMap[s.targetNodeId] = { id: s.targetNodeId, label: s.targetNodeName || s.targetNodeId?.substring(0, 8), type: s.targetNodeType || "concept" };
      });
      if (!nodeMap[nodeId]) nodeMap[nodeId] = { id: nodeId, label: "Center", type: "concept" };

      setNodes(Object.values(nodeMap));
      setEdges(allEdges.map((s) => ({
        source: s.sourceNodeId,
        target: s.targetNodeId,
        weight: s.weight || 0.5,
        relation: s.edgeName || s.relation || "",
      })));
    } catch (e) {
      console.warn("Failed to load neighborhood", e);
    } finally {
      setLoading(false);
    }
  }, [nodeId, graphId, depth]);

  useEffect(() => {
    fetchNeighborhood();
  }, [fetchNeighborhood]);

  useEffect(() => {
    if (nodes.length === 0) return;
    layoutAndRender();
  }, [nodes, edges]);

  const layoutAndRender = () => {
    const width = 560;
    const height = 400;
    const centerX = width / 2;
    const centerY = height / 2;

    const positions = {};
    const centerNode = nodes.find((n) => n.id === nodeId);
    const others = nodes.filter((n) => n.id !== nodeId);
    positions[nodeId] = { x: centerX, y: centerY };

    const radius = Math.min(width, height) * 0.35;
    others.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / others.length - Math.PI / 2;
      positions[n.id] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });

    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    const marker = document.createElementNS("http://www.w3.org/2000/svg", "marker");
    marker.setAttribute("id", "arrowhead");
    marker.setAttribute("markerWidth", "8");
    marker.setAttribute("markerHeight", "6");
    marker.setAttribute("refX", "8");
    marker.setAttribute("refY", "3");
    marker.setAttribute("orient", "auto");
    const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
    polygon.setAttribute("points", "0 0, 8 3, 0 6");
    polygon.setAttribute("fill", "#94a3b8");
    marker.appendChild(polygon);
    defs.appendChild(marker);
    svg.appendChild(defs);

    edges.forEach((e) => {
      const from = positions[e.source];
      const to = positions[e.target];
      if (!from || !to) return;
      const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
      line.setAttribute("x1", from.x);
      line.setAttribute("y1", from.y);
      line.setAttribute("x2", to.x);
      line.setAttribute("y2", to.y);
      line.setAttribute("stroke", "#94a3b8");
      line.setAttribute("stroke-width", Math.max(1, e.weight * 4));
      line.setAttribute("marker-end", "url(#arrowhead)");
      svg.appendChild(line);
    });

    nodes.forEach((n) => {
      const pos = positions[n.id];
      if (!pos) return;
      const isCenter = n.id === nodeId;
      const r = isCenter ? 24 : 16;
      const color = NODE_COLORS[n.type] || "#6366f1";

      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", pos.x);
      circle.setAttribute("cy", pos.y);
      circle.setAttribute("r", r);
      circle.setAttribute("fill", color);
      circle.setAttribute("stroke", isCenter ? "#1e1b4b" : "none");
      circle.setAttribute("stroke-width", isCenter ? "3" : "0");
      circle.style.cursor = "pointer";
      circle.addEventListener("click", () => onNodeClick?.(n.id));
      svg.appendChild(circle);

      const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
      text.setAttribute("x", pos.x);
      text.setAttribute("y", pos.y + r + 14);
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("font-size", "11");
      text.setAttribute("fill", "#334155");
      text.textContent = n.label.length > 18 ? n.label.substring(0, 16) + "…" : n.label;
      text.style.cursor = "pointer";
      text.addEventListener("click", () => onNodeClick?.(n.id));
      svg.appendChild(text);
    });
  };

  if (loading) return <div className="trace-empty">Loading neighborhood...</div>;
  if (nodes.length === 0 && !loading) return <div className="trace-empty">No connections found</div>;

  return (
    <div className="node-neighborhood-graph">
      <div className="node-neighborhood-graph__controls">
        <button
          type="button"
          className={`depth-btn ${depth === 1 ? "active" : ""}`}
          onClick={() => setDepth(1)}
        >
          1-hop
        </button>
        <button
          type="button"
          className={`depth-btn ${depth === 2 ? "active" : ""}`}
          onClick={() => setDepth(2)}
        >
          2-hop
        </button>
        <span className="node-count">{nodes.length} nodes, {edges.length} connections</span>
      </div>
      <svg
        ref={svgRef}
        width="100%"
        height="400"
        viewBox="0 0 560 400"
        className="node-neighborhood-graph__svg"
      />
    </div>
  );
};

export default NodeNeighborhoodGraph;
